import { joinUniqId } from '@futrli/shared-services';
import { xor } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RowType } from '../data-grid/types';
import { CellEditingMode, UserGridSettings } from './types';
import {
  addEditingRow,
  addExpandedRow,
  isRowInEditingMode,
  persistUserGridSettings,
  removeEditingRow,
  removeEditingRowCells,
  updateEditingRowCells
} from './user-grid-settings-actions';

export const useUserGridSettings = (storageValues: UserGridSettings) => {
  const editingRowCells = useRef<UserGridSettings['editingRowCells']>(storageValues.editingRowCells);
  const [expandedRows, setExpandedRows] = useState<UserGridSettings['expandedRows']>(storageValues.expandedRows);
  const [editingRows, setEditingRows] = useState<UserGridSettings['editingRows']>(storageValues.editingRows);
  const [rowHeights, setRowHeights] = useState<UserGridSettings['rowHeights']>(storageValues.rowHeights);
  const [seeded, setSeeded] = useState<UserGridSettings['seeded']>(storageValues.seeded);
  const [showDecimals, setShowDecimals] = useState<UserGridSettings['showDecimals']>(storageValues.showDecimals);
  const [showZeroValueAccountsMap, setShowZeroValueAccountsMap] = useState<
    UserGridSettings['showZeroValueAccountsMap']
  >(storageValues.showZeroValueAccountsMap);

  useEffect(() => {
    persistUserGridSettings({ expandedRows });
  }, [expandedRows]);
  useEffect(() => {
    persistUserGridSettings({ editingRows });
  }, [editingRows]);
  useEffect(() => {
    persistUserGridSettings({ rowHeights });
  }, [rowHeights]);
  useEffect(() => {
    persistUserGridSettings({ seeded });
  }, [seeded]);
  useEffect(() => {
    persistUserGridSettings({ showDecimals });
  }, [showDecimals]);

  const toggleExpandedRows = useCallback((...rowIds: (string | number)[]) => {
    setExpandedRows(expandedRows => xor(rowIds, expandedRows));
  }, []);

  const clearExpandedRows = useCallback(() => setExpandedRows([]), []);

  const clearEditingRows = () => setEditingRows([]);

  const activateEditingMode = (forecastId: string, parentRowId: string, rowId: string) => {
    // Make sure the parent row is expanded
    setExpandedRows(expandedRows => {
      const [result, isModified] = addExpandedRow(expandedRows, parentRowId);
      return isModified ? result : expandedRows;
    });

    setEditingRows(editingRows => {
      const [result, isModified] = addEditingRow(editingRows, forecastId, parentRowId, rowId);
      return isModified ? result : editingRows;
    });
  };

  const deactivateEditingMode = (forecastId: string, parentRowId: string, rowId: string) => {
    const [result, isModified] = removeEditingRowCells(editingRowCells.current, forecastId, parentRowId, rowId);

    if (isModified) {
      persistUserGridSettings({ editingRowCells: result });
      editingRowCells.current = result;
    }

    setEditingRows(editingRows => {
      const [result, isModified] = removeEditingRow(editingRows, forecastId, parentRowId, rowId);
      return isModified ? result : editingRows;
    });
  };

  const isInEditingMode = (forecastId: string, row: RowType) => {
    return isRowInEditingMode(editingRows, forecastId, row);
  };

  const updateEditingCell = (forecastId: string, parentRowId: string, rowId: string, cellData: CellEditingMode) => {
    const [result, isModified] = updateEditingRowCells(
      editingRowCells.current,
      forecastId,
      parentRowId,
      rowId,
      cellData
    );

    if (isModified) {
      persistUserGridSettings({ editingRowCells: result });
      editingRowCells.current = result;
    }
  };

  const getEditingRowCellValues = (forecastId: string, parentRowId: string, rowId: string) => {
    const uniqRowId = joinUniqId(forecastId, parentRowId, rowId);

    return editingRowCells.current[uniqRowId] ?? undefined;
  };

  const setShowZeroValueAccounts = (forecastId: string, value: boolean) => {
    if (!forecastId) {
      return;
    }
    setShowZeroValueAccountsMap(prev => {
      let newValue;
      if (value === false) {
        newValue = { ...prev, [forecastId]: value };
      } else {
        newValue = { ...prev };
        delete newValue[forecastId];
      }
      persistUserGridSettings({ showZeroValueAccountsMap: newValue });
      return newValue;
    });
  };

  const getShowZeroValueAccounts = (forecastId: string) => {
    if (!forecastId) {
      return true;
    }
    return showZeroValueAccountsMap[forecastId] !== false;
  };

  return {
    seeded,
    setSeeded,
    rowHeights,
    setRowHeights,
    expandedRows,
    toggleExpandedRows,
    clearExpandedRows,
    clearEditingRows,
    showDecimals,
    setShowDecimals,
    showZeroValueAccountsMap,
    setShowZeroValueAccountsMap,
    activateEditingMode,
    deactivateEditingMode,
    isInEditingMode,
    updateEditingCell,
    getEditingRowCellValues,
    getShowZeroValueAccounts,
    setShowZeroValueAccounts
  };
};
